import { INVENT_OAUTH_RESPONSE_TYPE, INVENT_OAUTH_SCOPES } from "../../../../lib/invent-oauth"

export type InventEnvironment = "development" | "integration" | "production"
export type InventRegion = "ca" | "us"

export interface InventOAuthConfig {
  environment: InventEnvironment
  region: InventRegion
  peaClientId: string
  authorizationBase: string
  callbackUrl: string
  responseType: string
  scopes: readonly string[]
}

export interface ValidatedInventOAuthRequest {
  state: string
  nonce: string
  codeChallenge: string
  prompt?: "login"
  clientSku?: string
  clientVersion?: string
}

type ValidationResult =
  | { ok: true; request: ValidatedInventOAuthRequest }
  | { ok: false; error: "invalid_request" | "configuration_unavailable" }

const REQUIRED_PARAMETERS = [
  "client_id",
  "redirect_uri",
  "response_type",
  "scope",
  "state",
  "nonce",
  "code_challenge",
  "code_challenge_method",
] as const

const OPTIONAL_PARAMETERS = ["prompt", "x-client-SKU", "x-client-ver"] as const
const ALLOWED_PARAMETERS = new Set<string>([...REQUIRED_PARAMETERS, ...OPTIONAL_PARAMETERS])
const METADATA_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/
const SAFE_OPAQUE_PATTERN = /^[\x21-\x7e]{1,1024}$/
const SAFE_CHALLENGE_PATTERN = /^[A-Za-z0-9._~-]{43,128}$/
const CONFIGURED_ENVIRONMENTS = new Set(["development", "integration", "production"])
const CONFIGURED_REGIONS = new Set(["ca", "us"])

function safeDecode(value: string): string | null {
  try {
    return decodeURIComponent(value.replaceAll("+", " "))
  } catch {
    return null
  }
}

function parseQuery(rawQuery: string): Map<string, string> | null {
  const query = rawQuery.startsWith("?") ? rawQuery.slice(1) : rawQuery
  if (!query) return null

  const values = new Map<string, string>()
  for (const pair of query.split("&")) {
    const separator = pair.indexOf("=")
    if (separator < 1) return null
    const name = safeDecode(pair.slice(0, separator))
    const value = safeDecode(pair.slice(separator + 1))
    if (!name || value === null || !ALLOWED_PARAMETERS.has(name) || values.has(name)) {
      return null
    }
    values.set(name, value)
  }
  return values
}

function isSafeHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return (
      url.protocol === "https:" &&
      url.username === "" &&
      url.password === "" &&
      url.hash === "" &&
      url.hostname.length > 0
    )
  } catch {
    return false
  }
}

function isValidConfiguration(config: InventOAuthConfig): boolean {
  if (
    !CONFIGURED_ENVIRONMENTS.has(config.environment) ||
    !CONFIGURED_REGIONS.has(config.region) ||
    !SAFE_OPAQUE_PATTERN.test(config.peaClientId) ||
    config.responseType !== INVENT_OAUTH_RESPONSE_TYPE ||
    !isSafeHttpsUrl(config.callbackUrl)
  ) {
    return false
  }

  try {
    const authorizationBase = new URL(config.authorizationBase)
    if (
      authorizationBase.protocol !== "https:" ||
      authorizationBase.username !== "" ||
      authorizationBase.password !== "" ||
      authorizationBase.search !== "" ||
      authorizationBase.hash !== "" ||
      (authorizationBase.pathname !== "" && authorizationBase.pathname !== "/") ||
      !/^[a-z0-9-]+\.auth\.[a-z0-9-]+\.amazoncognito\.com$/.test(authorizationBase.hostname)
    ) {
      return false
    }
  } catch {
    return false
  }

  return (
    config.scopes.length === INVENT_OAUTH_SCOPES.length &&
    new Set(config.scopes).size === INVENT_OAUTH_SCOPES.length &&
    INVENT_OAUTH_SCOPES.every((scope) => config.scopes.includes(scope))
  )
}

function hasExactScopes(actual: string, expected: readonly string[]): boolean {
  const scopes = actual.split(/\s+/).filter(Boolean)
  return (
    scopes.length === expected.length &&
    new Set(scopes).size === expected.length &&
    expected.every((scope) => scopes.includes(scope))
  )
}

export function validateInventOAuthRequest(
  rawQuery: string,
  config: InventOAuthConfig,
): ValidationResult {
  if (!isValidConfiguration(config)) {
    return { ok: false, error: "configuration_unavailable" }
  }

  const values = parseQuery(rawQuery)
  if (!values || REQUIRED_PARAMETERS.some((name) => !values.get(name))) {
    return { ok: false, error: "invalid_request" }
  }

  if (
    values.get("client_id") !== config.peaClientId ||
    values.get("redirect_uri") !== config.callbackUrl ||
    values.get("response_type") !== config.responseType ||
    !hasExactScopes(values.get("scope") ?? "", config.scopes) ||
    values.get("code_challenge_method") !== "S256"
  ) {
    return { ok: false, error: "invalid_request" }
  }

  const state = values.get("state") ?? ""
  const nonce = values.get("nonce") ?? ""
  const codeChallenge = values.get("code_challenge") ?? ""
  const prompt = values.get("prompt")
  const clientSku = values.get("x-client-SKU")
  const clientVersion = values.get("x-client-ver")

  if (
    !SAFE_OPAQUE_PATTERN.test(state) ||
    !SAFE_OPAQUE_PATTERN.test(nonce) ||
    !SAFE_CHALLENGE_PATTERN.test(codeChallenge) ||
    (prompt !== undefined && prompt !== "login") ||
    (clientSku !== undefined && !METADATA_PATTERN.test(clientSku)) ||
    (clientVersion !== undefined && !METADATA_PATTERN.test(clientVersion))
  ) {
    return { ok: false, error: "invalid_request" }
  }

  return {
    ok: true,
    request: {
      state,
      nonce,
      codeChallenge,
      ...(prompt === "login" ? { prompt } : {}),
      ...(clientSku ? { clientSku } : {}),
      ...(clientVersion ? { clientVersion } : {}),
    },
  }
}

export function buildInventAuthorizationUrl(
  config: InventOAuthConfig,
  request: ValidatedInventOAuthRequest,
): string {
  if (!isValidConfiguration(config)) {
    throw new Error("INVENT OAuth configuration is unavailable")
  }
  const target = new URL("/oauth2/authorize", config.authorizationBase)
  target.searchParams.set("client_id", config.peaClientId)
  target.searchParams.set("redirect_uri", config.callbackUrl)
  target.searchParams.set("response_type", config.responseType)
  target.searchParams.set("scope", config.scopes.join(" "))
  target.searchParams.set("state", request.state)
  target.searchParams.set("nonce", request.nonce)
  target.searchParams.set("code_challenge", request.codeChallenge)
  target.searchParams.set("code_challenge_method", "S256")
  if (request.prompt) target.searchParams.set("prompt", request.prompt)
  if (request.clientSku) target.searchParams.set("x-client-SKU", request.clientSku)
  if (request.clientVersion) target.searchParams.set("x-client-ver", request.clientVersion)
  return target.toString()
}

export function createInventOnboardingAttempt(
  request: ValidatedInventOAuthRequest,
  createdAt = Date.now(),
  ttlMs = 10 * 60 * 1000,
) {
  let retained: ValidatedInventOAuthRequest | null = request
  const expiresAt = createdAt + ttlMs

  return {
    current(now = Date.now()): ValidatedInventOAuthRequest | null {
      if (now > expiresAt) retained = null
      return retained
    },
    end(_reason: "forwarded" | "cancelled" | "failed"): void {
      retained = null
    },
  }
}
